import { useMemo } from 'react';

/**
 * Count keys whose value differs from the initial/default filter value.
 *
 * Handles strings, numbers, booleans, and arrays. Arrays compare by
 * sorted-stringify; primitives compare by `===`. The `search` key is
 * always excluded — it has its own visible input on mobile so it
 * shouldn't inflate the filter-sheet badge.
 */
export function useActiveFilterCount<F extends object>(
  current: F,
  defaults: F,
  ignoreKeys: (keyof F)[] = ['search' as keyof F],
): number {
  return useMemo(() => {
    const ignored = new Set(ignoreKeys as string[]);
    let n = 0;
    for (const k of Object.keys(defaults as object)) {
      if (ignored.has(k)) continue;
      const a = (current as Record<string, unknown>)[k];
      const b = (defaults as Record<string, unknown>)[k];
      if (Array.isArray(a) || Array.isArray(b)) {
        const aa = Array.isArray(a) ? [...a].sort() : [];
        const bb = Array.isArray(b) ? [...b].sort() : [];
        if (JSON.stringify(aa) !== JSON.stringify(bb)) n += 1;
      } else if (a !== b) {
        n += 1;
      }
    }
    return n;
  }, [current, defaults, ignoreKeys]);
}
